
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { LAYOUT_TYPE } from '../../model/common.model';
import { DynamicCardBannerComponent } from '../dynamic-card-banner/dynamic-card-banner.component';
import { PageContainerComponent } from '../page-container/page-container.component';
import { TabConfig, Props } from '../../model/form.model';
import { HomeTabConfigIds, HomeProps, HomeScreenBannerSearchConfig, TurnOverReportSearchConfig, HomePageCardDetails } from '../../model/home.model';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { Component, inject, OnInit } from '@angular/core';
import { CommonUtils } from '../../utils/common.utils';
import { CommonService } from '../../services/common.service';
import { CblHighlightRow, DbFieldKeys, getQueryDatesForTurnOverReport, SearchKeys, Skip, TurnOverReportDataCalculationConfig, TurnOverTabIds } from '../../model/turn-over.model';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    MatCardModule, MatTabsModule, MatTableModule, PageContainerComponent,
    DynamicCardBannerComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit{
  public isDataLoading = false;
  private readonly commonUtils = inject(CommonUtils);
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  tabSchema: Array<TabConfig> = structuredClone(HomeTabConfigIds);
  props: Props = HomeProps;
  public turnOverReportLabel = "Turnover report from 03/01/2024 to 02/28/2025";
  public beginRender = false;
  private readonly service = inject(CommonService);
  private readonly bannerSearchConfig: SearchConfig = HomeScreenBannerSearchConfig;
  cardDetails = HomePageCardDetails;
  public turnOverResponse = {} as any;
  public isLoading = false;
  clonedArray: any;
  ngOnInit() {
    this.initialize()
    this.fetchAllData();
    this.beginRender = true
  }
  initialize(){
    this.updateLabel();
    this.tabSchema.forEach(tab=>{
      if(tab['inputs']['DataTableSchema']){
        tab['inputs']['DataTableSchema'].isExportRequired = false;
        tab['inputs']['DataTableSchema'].isSearchRequired = false;
      }
    })
  }
  formatDate = (dateStr: string) => {
    const mmddyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    if (mmddyyyyPattern.test(dateStr)) {
      return dateStr;
    }
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  }
  updateLabel() {
    const startDate = getQueryDatesForTurnOverReport('start');
    const endDate = getQueryDatesForTurnOverReport('end')
    this.turnOverReportLabel = `Turnover Report for Diagnostic Specialist/Service Technician from ${this.formatDate(startDate)} to ${this.formatDate(endDate)}`
  }
  getBannerData() {
    this.service.getDataFromDataSource(this.bannerSearchConfig, ApiEnvNames['TurnOverReport']).then(banner => {
      if (banner['status'] === API_STATUS['Error']) {
        this.cardDetails.forEach(card => {
          card['isLoading'] = false
        })
      }
      if (banner['status'] === API_STATUS['Success']) {
        const bannerCount = banner['result']?.[0] ?? {};
        this.cardDetails.forEach(card => {
          card['label'] = bannerCount[card['id']] ?? 0
          card['isLoading'] = false
        })
      }
      return banner
    }).catch(err=>{
      this.cardDetails.forEach(card => {
        card['label'] = 0;
        card['isLoading'] = false;
      })
      console.info(`${ERROR_RESPONSE['layoutErrorPrefix']} Failed to fetch banner data`,err);
    })
  }
  fetchTurnOverData(searchConfig:SearchConfig, key:string){
    const errorHandler = (msg:any)=>{
      this.turnOverResponse[key] = []
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      return Promise.reject(new Error(msg));
    }
    return this.service.getDataFromDataSource(searchConfig, ApiEnvNames['TurnOverReport']).then(res => {
      if(res['status'] === API_STATUS['Success']) {
        this.turnOverResponse[key] = res['result']
        return this.turnOverResponse;
      }else {
        return errorHandler(res['errorMsg'])
      }
    }).catch(err=>{
      return errorHandler(err)
    })
  }
  async fetchAllData() {
    this.isLoading = true;
    this.tabSchema.forEach(tab => {
      if (tab['inputs']['DataTableSchema']) {
        tab['inputs']['DataTableSchema'].isLoading = true;
      }
    });
    this.getBannerData();
    const searchConfigs = this.getPayload();
    const taskList: any[] = [];
    this.turnOverResponse = {};
    Object.keys(searchConfigs).forEach(searchConfigKey => {
      taskList.push(this.fetchTurnOverData(searchConfigs[searchConfigKey], searchConfigKey));
    })
    return Promise.all(taskList).then(() => {
        this.handleTurnOverReportResult(this.turnOverResponse);
        this.tabSchema.forEach(tab => {
          if (tab['inputs']['DataTableSchema']) {
            tab['inputs']['DataTableSchema'].isLoading = false;
          }
        });
        this.isLoading = false;
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      this.tabSchema.forEach(tab => {
        if (tab['inputs']['DataTableSchema']) {
          tab['inputs']['DataTableSchema'].isLoading = false;
        }
      });
      this.isLoading = false;
    })
  }
  getPayload = () => {
    const postParam = structuredClone(TurnOverReportSearchConfig)
    Object.values(postParam).forEach(searchConfig => {
      searchConfig['body']['endDate'] = getQueryDatesForTurnOverReport('end')
      searchConfig['body']['startDate'] = getQueryDatesForTurnOverReport('start')
    })
    return postParam;
  }
  handleTurnOverReportResult(dataSet: any) {
    this.tabSchema.forEach(tab => {
      if (tab.inputs?.DataTableSchema) {
        tab['inputs']['dataInput'] = [];
      }
      let dataSource = []
      if (tab['searchKey'] === SearchKeys['Area']) {
        dataSource = this.handleResponseForArea(tab['tabId'], dataSet);
      } else if (TurnOverTabIds['areaCertification'] === tab['tabId']) {
        dataSource = this.handleResponseForArea(tab['tabId'], dataSet);
      } else if (TurnOverTabIds['nationalCertification'] === tab['tabId']) {
        dataSource = this.handleNationalCert(dataSet);
      }
      if (tab.inputs?.DataTableSchema) {
        tab.inputs.DataTableSchema.dataSource = dataSource
      }
      tab['inputs']['dataInput'] = dataSource
    })
  }
  handleResponseForArea(id: string, dataSet: any) {
    if (id === TurnOverTabIds['area']) {
      const data: any = this.formatDataForReport(dataSet[SearchKeys['Area']], { key: 'areaName', label: 'National' })
      return [...data['records'], ...data['consolidateData']]
    } else if (id === TurnOverTabIds['areaCertification']) {
      const data = structuredClone(dataSet[SearchKeys['Certificate']]);
      const result: any = this.groupAndFormatDataForReport(data, { key: 'areaName', label: 'National', innerKey: 'certLevel' });
      return [...result['records'], ...result['consolidateData']]
    } else {
      return this.handleNationalCert(dataSet);
    }
  }
  handleNationalCert(dataSet: any) {
    const certData: any[] = structuredClone(dataSet[SearchKeys['Certificate']]);
    const result: any = {};
    const summary: any = { certLevel: 'Total', areaName: 'National' };
    certData.forEach(entry => {
      const certLevel = entry.certLevel;
      result[certLevel] ??= { certLevel };
      for (const key in entry) {
        if (key !== 'certLevel' && key !== 'areaName') {
          const value = Number.parseFloat(entry[key]);
          if (!Number.isNaN(value)) {
            result[certLevel][key] = (result[certLevel][key] || 0) + value;
            summary[key] = (summary[key] ?? 0) + value;
          }
        }
      }
      result[certLevel]['areaName'] = certLevel;
    });
    Object.values(result).forEach((data:any) => {
      Object.keys(data).forEach(fieldKey => {
        if (fieldKey.includes('Percent')) {
          const key = fieldKey.split('Percent')[0]
          const percent = (data[key] / data['startPeriod']) * 100
          data[fieldKey] = percent.toFixed(2)
        }
      })
    })
    Object.keys(summary).forEach(fieldKey => {
      if (fieldKey.includes('Percent')) {
        const key = fieldKey.split('Percent')[0]
        const percent = (summary[key] / summary['startPeriod']) * 100
        summary[fieldKey] = percent.toFixed(2)
      }
    })
    summary[CblHighlightRow] = true;
    return [summary, ...Object.values(result)];
  }
  groupAndFormatDataForReport(records: any[], displayConfig: { key: string, label: string, innerKey: string }, consolidateRequired = true) {
    if (records.length <= 0) {
      return {
        records: [],
        consolidateData: []
      }
    }
    const result: any = {}
    records.forEach((record: any) => {
      const groupKey = record[displayConfig['key']]
      result[groupKey] ??= [];
      result[groupKey].push(record)
    })
    const dataArr: any[] = [];
    let consolidateData: any = {};
    const calcConfig = TurnOverReportDataCalculationConfig;
    const fieldNames = Object.keys(calcConfig);
    const sumAllFields = (data: any, result: any) => {
      for (const key in data) {
        if (fieldNames.includes(key)) {
          const value = Number.parseFloat(data[key]);
          if (Number.isNaN(value)) {
            result[key] = data[key];
          } else {
            result[key] = (result[key] ?? 0) + value;
          }
        } else {
          result[key] = ''
        }
      }
      return result
    }
    Object.keys(result).forEach(groupKey => {
      const data: any = this.formatDataForReport(result[groupKey], { key: displayConfig['key'], label: groupKey, innerKey: 'certLevel' })
      dataArr.push(...data['consolidateData'], ...data['records'])
      consolidateData = sumAllFields(data['consolidateData'][0], consolidateData)
    })
    const percentageFields = fieldNames.filter(field => calcConfig[field] !== Skip);
    if (percentageFields.length > 0) {
      percentageFields.forEach(fieldName => {
        let value: any = 0;
        if (consolidateData[DbFieldKeys['StartPeriod']] !== 0) {
          value = (Math.round(consolidateData[fieldName] * 100) / consolidateData[DbFieldKeys['StartPeriod']]).toFixed(2)
        }
        consolidateData[calcConfig[fieldName]] = value;
      })
    }
    consolidateData[displayConfig['key']] = displayConfig['label'];
    consolidateData[CblHighlightRow] = true;
    let groupedData: any[] = []
    if (consolidateRequired) {
      groupedData = [consolidateData]
    }
    return {
      records: dataArr,
      consolidateData: groupedData
    }
  }
  formatDataForReport(records: any[], displayConfig: { key: string, label: string, innerKey?: string, labelKey?: string }) {
    if (records.length <= 0) {
      return {
        records: [],
        consolidateData: []
      }
    }
    let consolidateData: any = {};
    const calcConfig = TurnOverReportDataCalculationConfig;
    const fieldNames = Object.keys(calcConfig);
    const sumAllFields = (data: any, result: any) => {
      for (const key in data) {
        if (fieldNames.includes(key)) {
          const value = Number.parseFloat(data[key]);
          if (Number.isNaN(value)) {
            result[key] = data[key];
          } else {
            result[key] = (result[key] ?? 0) + value;
          }
        } else {
          result[key] = data[key]
        }
      }
      return result
    }
    records.forEach(record => {
      consolidateData = sumAllFields(record, consolidateData)
      if (displayConfig['innerKey']) {
        record[displayConfig['key']] = record[displayConfig['innerKey']]
      }
    })
    const percentageFields = fieldNames.filter(field => calcConfig[field] !== Skip);
    if (percentageFields.length > 0) {
      percentageFields.forEach(fieldName => {
        let value: any = 0;
        if (consolidateData[DbFieldKeys['StartPeriod']] !== 0) {
          value = (Math.round(consolidateData[fieldName] * 100) / consolidateData[DbFieldKeys['StartPeriod']]).toFixed(2)
        }
        consolidateData[calcConfig[fieldName]] = value;
      })
    }
    if (displayConfig['labelKey']) {
      consolidateData[displayConfig['key']] = consolidateData[displayConfig['labelKey']];
    } else {
      consolidateData[displayConfig['key']] = displayConfig['label'];
    }
    consolidateData[CblHighlightRow] = true;
    return {
      records: records,
      consolidateData: [consolidateData]
    }
  }
}
